/**
 * Derives the list of refs the ConditionBuilder exposes from existing
 * admin endpoints:
 *
 *   - Registration fields → `data_capture_config.field_overrides`
 *     + the static customer/transaction defaults.
 *   - Entity attributes   → product-template HOST-level attributes.
 *   - Aggregates / roles  → static list for now; S4 extends.
 *
 * The hook is intentionally tolerant: every source is optional and
 * returns an empty list if not yet loaded. The builder still renders -
 * just with fewer options.
 */

import { useMemo } from 'react'
import {
  useRegistrationSettings,
  type DataCaptureFieldKey,
  type FieldOverride,
} from '@/admin/hooks/use-registration-settings'
import { useProductTemplates } from '@/admin/hooks/use-products'
import { useEntityTypes } from '@/admin/hooks/use-entities'
import { useHostTemplates } from '@/admin/hooks/use-host-templates'
import { usePartnershipAttributes } from '@/admin/hooks/use-partnership-attributes'
import { useRegistrationFields } from '@/admin/hooks/use-registration-fields'
import {
  useWarrantyCategories,
  useWarrantyTemplates,
} from '@/admin/hooks/use-warranties'
import type { AvailableRef, RefType } from './types'

// ---------------------------------------------------------------------------
// Static defaults - the canonical set of customer/transaction fields that
// live on every registration before template-level overrides.
// ---------------------------------------------------------------------------

interface StaticField {
  key: DataCaptureFieldKey
  label: string
  type: RefType
}

const STATIC_FIELDS: StaticField[] = [
  { key: 'consumer_email', label: 'Customer email', type: 'string' },
  { key: 'consumer_name', label: 'Customer name', type: 'string' },
  { key: 'consumer_phone', label: 'Customer phone', type: 'string' },
  { key: 'consumer_address', label: 'Customer address', type: 'string' },
  { key: 'transaction_date', label: 'Purchase date', type: 'date' },
  { key: 'transaction_price', label: 'Purchase price', type: 'number' },
  {
    key: 'transaction_proof_of_purchase',
    label: 'Proof of purchase',
    type: 'string',
  },
]

const STATIC_AGGREGATES: AvailableRef[] = [
  {
    ref: 'aggregate:claims.count',
    label: 'Claims for this host',
    category: 'aggregate',
    type: 'number',
  },
  {
    ref: 'aggregate:registrations.count',
    label: 'Prior registrations by this customer',
    category: 'aggregate',
    type: 'number',
  },
]

const STATIC_ROLES: AvailableRef[] = [
  { ref: 'role:admin', label: 'Admin', category: 'role', type: 'boolean' },
  { ref: 'role:staff', label: 'Staff', category: 'role', type: 'boolean' },
]

// ---------------------------------------------------------------------------
// Field-type mapping - backend uses SCREAMING_CASE, builder uses lowercase.
// ---------------------------------------------------------------------------

function mapFieldType(backendType: string | undefined): RefType {
  if (!backendType) return 'string'
  const t = backendType.toUpperCase()
  if (['NUMBER', 'MONEY'].includes(t)) return 'number'
  if (['DATE', 'DATE_TIME'].includes(t)) return 'date'
  if (t === 'BOOLEAN') return 'boolean'
  if (t === 'MULTI_SELECT') return 'multi_enum'
  if (t === 'SELECT') return 'enum'
  return 'string'
}

type RawOption = { value: unknown; label: unknown }
type RawOptionGroup =
  | RawOption[]
  | { depends_on?: unknown; option_groups?: Record<string, RawOption[]> }

function _normaliseOption(o: unknown): { value: string; label: string } | null {
  if (typeof o !== 'object' || o === null) return null
  if (!('value' in o) || !('label' in o)) return null
  return { value: String((o as RawOption).value), label: String((o as RawOption).label) }
}

function extractOptions(
  configData: Record<string, unknown> | undefined,
): { value: string; label: string }[] | undefined {
  const flat = Array.isArray(configData?.options)
    ? (configData?.options as RawOption[]).map(_normaliseOption).filter(Boolean)
    : []
  // Dependent SELECT fields (e.g. claim.major_category, which switches its
  // option set based on product_impacted) keep the full catalogue under
  // ``option_groups`` and leave ``options`` as a trivial fallback. The
  // trigger condition builder has no claim context to pick a group with,
  // so merge every group into the catalogue - admins can pick any value.
  const groups = configData?.option_groups
  if (groups && typeof groups === 'object') {
    for (const value of Object.values(groups as Record<string, RawOptionGroup>)) {
      if (Array.isArray(value)) {
        flat.push(...value.map(_normaliseOption).filter(Boolean))
      } else if (
        value &&
        typeof value === 'object' &&
        'option_groups' in value &&
        value.option_groups
      ) {
        // Doubly-nested (e.g. claim.major_category → component → component_type).
        for (const inner of Object.values(value.option_groups)) {
          if (Array.isArray(inner)) {
            flat.push(...inner.map(_normaliseOption).filter(Boolean))
          }
        }
      }
    }
  }
  if (flat.length === 0) return undefined

  // De-dupe by value (groups may legitimately share entries).
  const seen = new Set<string>()
  const unique: { value: string; label: string }[] = []
  for (const opt of flat) {
    if (!opt || seen.has(opt.value)) continue
    seen.add(opt.value)
    unique.push(opt)
  }
  return unique
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseAvailableRefsResult {
  refs: AvailableRef[]
  isLoading: boolean
  /** Look up a ref by its raw string - used by the op/value pickers. */
  byRef: (ref: string) => AvailableRef | undefined
}

export function useAvailableRefs(
  options: { enabled?: boolean; entityKey?: string } = {},
): UseAvailableRefsResult {
  const enabled = options.enabled ?? true
  const entityKey = options.entityKey
  const { data: settings, isLoading: loadingSettings } = useRegistrationSettings({
    enabled,
  })
  const { data: templates, isLoading: loadingTemplates } = useProductTemplates({
    enabled,
  })
  // /products/templates/ only embeds a `HostTemplateSummary` (no
  // attributes), so host-template fields would never appear in the ref
  // catalog if we only walked that. Fetch the full host templates
  // separately so condition rules referencing host attributes (e.g.
  // `entity:host.year_built`) resolve to a labelled, typed ref
  // instead of falling back to the raw string.
  const { data: hostTemplates, isLoading: loadingHostTemplates } =
    useHostTemplates({ enabled })
  const { data: entityTypes, isLoading: loadingEntityTypes } = useEntityTypes(
    undefined,
    { enabled },
  )
  const { data: partnershipAttributes, isLoading: loadingPartnershipAttributes } =
    usePartnershipAttributes({ enabled })
  const { data: regFields, isLoading: loadingRegFields } = useRegistrationFields({
    enabled,
  })
  // Warranty categories feed the `entity:warranty_template.category_slug`
  // enum options; warranty templates feed the per-template attribute
  // refs (`entity:warranty_template.attributes.<code_name>`). Both are
  // rendered under a dedicated "Warranty fields" group so predicates
  // authored here line up with the
  // `warranty_entities_for_option` rule context builder on the backend.
  const { data: warrantyCategories, isLoading: loadingWarrantyCategories } =
    useWarrantyCategories({ enabled })
  const { data: warrantyTemplates, isLoading: loadingWarrantyTemplates } =
    useWarrantyTemplates(undefined, { enabled })

  const refs = useMemo<AvailableRef[]>(() => {
    const out: AvailableRef[] = []
    const overrides: Record<string, FieldOverride> =
      settings?.data_capture_config?.field_overrides ?? {}

    // Registration fields (customer + transaction) with overrides applied.
    for (const f of STATIC_FIELDS) {
      const ov = overrides[f.key]
      out.push({
        ref: `field:${f.key}`,
        label: ov?.label ?? f.label,
        category: 'registration_field',
        type: ov?.field_type ? mapFieldType(ov.field_type) : f.type,
      })
    }

    const seen = new Set<string>()

    // HOLDING-level attributes from every product template (de-duped by
    // code_name so cross-template duplicates don't render twice).
    for (const tmpl of templates ?? []) {
      for (const attr of tmpl.attributes ?? []) {
        if (attr.attribute_level !== 'HOLDING') continue
        if (seen.has(attr.code_name)) continue
        seen.add(attr.code_name)
        out.push({
          ref: `field:${attr.code_name}`,
          label: attr.display_name,
          category: 'registration_field',
          type: mapFieldType(attr.field_type),
          options: extractOptions(attr.configuration_data),
        })
      }
    }

    const currentEntity = entityKey
      ? (entityTypes ?? []).find((candidate) => candidate.key === entityKey)
      : null
    const entityRefKeys = new Set<string>()
    if (currentEntity) {
      entityRefKeys.add(currentEntity.key)
      for (const relationship of currentEntity.relationships ?? []) {
        entityRefKeys.add(relationship.target_key)
      }
    }

    // Entity attributes come from the org-scoped entity catalog. Legacy
    // host-template refs are still added below so existing rules remain
    // discoverable while seeded data migrates.
    for (const entity of entityTypes ?? []) {
      if (currentEntity && !entityRefKeys.has(entity.key)) continue
      for (const attr of entity.attributes ?? []) {
        if (attr.configuration_data?.value_source === 'entity_relationship') continue
        if (currentEntity && entity.key === currentEntity.key) {
          const fieldRef = `field:${attr.code_name}`
          if (!seen.has(fieldRef)) {
            seen.add(fieldRef)
            out.push({
              ref: fieldRef,
              label: attr.display_name,
              category: 'registration_field',
              type: mapFieldType(attr.field_type),
              options: extractOptions(attr.configuration_data),
            })
          }
        }
        const ref = `entity:${entity.key}.${attr.code_name}`
        if (seen.has(ref)) continue
        seen.add(ref)
        out.push({
          ref,
          label: `${entity.label} · ${attr.display_name}`,
          category: 'entity',
          type: mapFieldType(attr.field_type),
          options: extractOptions(attr.configuration_data),
        })
      }
      for (const field of entity.metadata?.registration?.custom_fields ?? []) {
        if (currentEntity && entity.key !== currentEntity.key) continue
        const ref = `field:${field.code_name}`
        if (seen.has(ref)) continue
        seen.add(ref)
        out.push({
          ref,
          label: `${entity.label} · ${field.display_name}`,
          category: 'custom_registration_field',
          type: mapFieldType(field.field_type),
          options: extractOptions(field.configuration_data),
        })
      }
    }

    // Host-template attributes remain as a compatibility surface for existing
    // `entity:host.*` predicates.
    for (const hostTmpl of hostTemplates ?? []) {
      for (const hostAttr of hostTmpl.attributes ?? []) {
        const ref = `entity:host.${hostAttr.code_name}`
        if (seen.has(ref)) continue
        seen.add(ref)
        out.push({
          ref,
          label: `Host · ${hostAttr.display_name}`,
          category: 'entity',
          type: mapFieldType(hostAttr.field_type),
          options: extractOptions(hostAttr.configuration_data),
        })
      }
    }

    // Partnership attributes resolve into the generic contractor entity so
    // warranty-filter rules can target the acting contractor without any
    // tenant-specific hardcoding in the engine.
    for (const attr of partnershipAttributes ?? []) {
      const ref = `entity:contractor.${attr.code_name}`
      if (seen.has(ref)) continue
      seen.add(ref)
      out.push({
        ref,
        label: `Contractor · ${attr.display_name}`,
        category: 'entity',
        type: mapFieldType(attr.field_type),
        options: extractOptions(attr.configuration_data),
      })
    }

    // Tenant-scoped custom registration fields (from RegistrationField model).
    for (const rf of regFields ?? []) {
      if (!rf.is_active) continue
      const ref = `field:${rf.code_name}`
      if (seen.has(rf.code_name)) continue
      seen.add(rf.code_name)
      out.push({
        ref,
        label: rf.display_name,
        category: 'custom_registration_field',
        type: mapFieldType(rf.field_type),
        options: extractOptions(rf.configuration_data),
      })
    }

    // Warranty category - the consumer-selected warranty's category slug.
    // Backed by `entity:warranty_template.category_slug` (string) with
    // options sourced from `WarrantyCategory` rows. Exposed as an enum
    // so the value picker renders a Select instead of a free-text input.
    const categoryOptions = (warrantyCategories ?? [])
      .filter((c) => c.slug)
      .map((c) => ({ value: c.slug, label: c.name }))
    if (categoryOptions.length > 0) {
      out.push({
        ref: 'entity:warranty_template.category_slug',
        label: 'Warranty category',
        category: 'warranty_field',
        type: 'enum',
        options: categoryOptions,
      })
    } else {
      out.push({
        ref: 'entity:warranty_template.category_slug',
        label: 'Warranty category',
        category: 'warranty_field',
        type: 'string',
      })
    }

    // Core template metadata already populated by
    // `warranty_entities_for_option` - safe to expose as predicate refs.
    out.push(
      {
        ref: 'entity:warranty_template.name',
        label: 'Warranty template name',
        category: 'warranty_field',
        type: 'string',
      },
      {
        ref: 'entity:warranty_template.policy_code',
        label: 'Warranty policy code',
        category: 'warranty_field',
        type: 'string',
      },
      {
        ref: 'entity:warranty_template.warranty_type',
        label: 'Warranty type',
        category: 'warranty_field',
        type: 'string',
      },
    )

    // Warranty attributes (EAV) - dedup by code_name across templates so
    // a shared `coverage_years` attribute renders once. Resolved at
    // eval time via `entity:warranty_template.attributes.<code_name>`.
    const warrantyAttrSeen = new Set<string>()
    for (const tmpl of warrantyTemplates ?? []) {
      for (const attr of tmpl.attributes ?? []) {
        if (warrantyAttrSeen.has(attr.code_name)) continue
        warrantyAttrSeen.add(attr.code_name)
        out.push({
          ref: `entity:warranty_template.attributes.${attr.code_name}`,
          label: `Warranty · ${attr.display_name}`,
          category: 'warranty_field',
          type: mapFieldType(attr.field_type),
          options: extractOptions(attr.configuration_data),
        })
      }
    }

    out.push(...STATIC_AGGREGATES, ...STATIC_ROLES)
    return out
  }, [
    settings?.data_capture_config?.field_overrides,
    templates,
    entityTypes,
    entityKey,
    hostTemplates,
    partnershipAttributes,
    regFields,
    warrantyCategories,
    warrantyTemplates,
  ])

  const byRef = useMemo(() => {
    const map = new Map(refs.map((r) => [r.ref, r]))
    return (ref: string) => map.get(ref)
  }, [refs])

  return {
    refs,
    isLoading:
      loadingSettings ||
      loadingTemplates ||
      loadingEntityTypes ||
      loadingHostTemplates ||
      loadingPartnershipAttributes ||
      loadingRegFields ||
      loadingWarrantyCategories ||
      loadingWarrantyTemplates,
    byRef,
  }
}
